/**
 * Pure helpers for the schedule-ingestion review step.
 *
 * Kept free of React so the team-resolution logic (the part with real rules)
 * can be unit-tested in isolation. See scheduleReview.test.ts.
 */

import type {
  ExtractedGame,
  TeamMatch,
  TeamResolution,
} from "../../../../types/schedule-ingestion";
import { directionsUrl } from "../../../../services/calendar";
import { timeZoneAbbrev } from "../../../../utils/calendarTime";

/**
 * What the user chose for a single DISTINCT raw team name:
 * - `unmatched`: leave the side blank (no team linked, none created).
 * - `existing`: link to an existing team by `teamId`.
 * - `create`: create a new team named after the raw name on confirm.
 */
export type TeamChoiceKind = "unmatched" | "existing" | "create";

export interface TeamChoice {
  kind: TeamChoiceKind;
  /** Set only when kind === "existing". */
  teamId?: string;
}

/** Encoded dropdown option values, kept stable so the <select> can round-trip. */
export const UNMATCHED_VALUE = "__unmatched__";
export const CREATE_NEW_VALUE = "__create_new__";

/**
 * US time zones offered by the schedule review's timezone picker: the IANA id (the value the backend
 * stores and renders games in) with a friendly label. Covers the distinct US zones, including the
 * non-DST Arizona zone and the Alaska/Hawaii zones.
 */
export const US_TIME_ZONES: readonly { id: string; label: string }[] = [
  { id: "America/New_York", label: "Eastern (New York)" },
  { id: "America/Chicago", label: "Central (Chicago)" },
  { id: "America/Denver", label: "Mountain (Denver)" },
  { id: "America/Phoenix", label: "Arizona — no DST (Phoenix)" },
  { id: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { id: "America/Anchorage", label: "Alaska (Anchorage)" },
  { id: "America/Adak", label: "Hawaii–Aleutian (Adak)" },
  { id: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];

/**
 * The initial choice for a raw name: honor the server's suggested match when
 * present and the name is NOT ambiguous. Otherwise — no match found, or an
 * ambiguous name with several candidates — default to UNMATCHED (the game keeps
 * its raw name with no team linked). We do NOT auto-create teams from the
 * schedule, because that spawns hundreds of rosterless teams; the user can still
 * open the dropdown to link an existing team or explicitly create one.
 */
export function initialChoiceFor(match: TeamMatch): TeamChoice {
  if (!match.isAmbiguous && match.suggestedMatch) {
    return { kind: "existing", teamId: match.suggestedMatch.teamId };
  }
  return { kind: "unmatched" };
}

/**
 * Decode a dropdown <select> value into a TeamChoice. Any real team id maps to
 * an "existing" choice; the two sentinels map to their kinds.
 */
export function choiceFromSelectValue(value: string): TeamChoice {
  if (value === CREATE_NEW_VALUE) return { kind: "create" };
  if (value === UNMATCHED_VALUE || value === "") return { kind: "unmatched" };
  return { kind: "existing", teamId: value };
}

/** Encode a TeamChoice back to its <select> value. */
export function selectValueFromChoice(choice: TeamChoice): string {
  if (choice.kind === "create") return CREATE_NEW_VALUE;
  if (choice.kind === "existing" && choice.teamId) return choice.teamId;
  return UNMATCHED_VALUE;
}

/**
 * Build the confirm payload's `teamResolutions` from the user's per-name
 * choices: exactly one entry per DISTINCT raw name, in first-seen order.
 *
 * - "existing" -> `{ rawName, teamId }`
 * - "create"   -> `{ rawName, createNew: true, newTeamName: rawName }`
 * - "unmatched"-> `{ rawName }` (server leaves the side blank)
 *
 * Blank / whitespace-only raw names are skipped entirely: an empty team side
 * on a game is not a name to resolve.
 */
export function buildTeamResolutions(
  distinctRawNames: string[],
  choices: Map<string, TeamChoice>
): TeamResolution[] {
  const seen = new Set<string>();
  const resolutions: TeamResolution[] = [];

  for (const rawName of distinctRawNames) {
    const trimmed = rawName.trim();
    if (trimmed.length === 0) continue;
    if (seen.has(rawName)) continue;
    seen.add(rawName);

    const choice = choices.get(rawName) ?? { kind: "unmatched" };

    if (choice.kind === "existing" && choice.teamId) {
      resolutions.push({ rawName, teamId: choice.teamId });
    } else if (choice.kind === "create") {
      resolutions.push({ rawName, createNew: true, newTeamName: rawName });
    } else {
      resolutions.push({ rawName });
    }
  }

  return resolutions;
}

/**
 * Build a Google Maps directions link for a game's resolved field, reusing the
 * calendar's `directionsUrl` builder so the link format stays identical:
 * place id preferred, then coordinates, then the raw field text. Returns null
 * only when there is nothing at all to point at (no address AND no raw name).
 */
export function fieldDirectionsUrl(game: ExtractedGame): string | null {
  const rawField = game.field.trim();
  return directionsUrl({
    locationPlaceId: game.fieldPlaceId,
    locationLat: game.fieldLat,
    locationLng: game.fieldLng,
    locationAddress: game.fieldAddress,
    locationName: rawField.length > 0 ? rawField : null,
  });
}

/**
 * The time label to show for a game: the extracted wall-clock time text plus
 * the field timezone's short label, e.g. `"1:00 PM EDT"`. The extracted `time`
 * is free-form text (not a UTC instant), so we append the tz label the same way
 * the calendar's `formatEventTime` does rather than reformatting the time.
 *
 * - A blank time returns the empty string (the row shows its own placeholder).
 * - When the tz label can't be resolved, just the time text is returned.
 */
export function gameTimeLabel(game: ExtractedGame): string {
  const time = game.time.trim();
  if (time.length === 0) return "";
  const abbrev = timeZoneAbbrev(game.timeZone);
  return abbrev ? `${time} ${abbrev}` : time;
}
